import { useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { createClaim, getClaim, updateClaim } from '../services/insuranceService';
import { getClients, getClient } from '../services/clientService';
import { getInvoices } from '../services/invoiceService';
import { getAppointment } from '../services/appointmentService';
import { getFeeScheduleItems } from '../services/feeScheduleService';
import Spinner from '../components/common/Spinner';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PLACE_OF_SERVICE_CODES = [
  { code: '02', label: '02 — Telehealth (patient off-site)' },
  { code: '10', label: '10 — Telehealth (patient home)' },
  { code: '11', label: '11 — Office' },
  { code: '12', label: '12 — Home' },
  { code: '21', label: '21 — Inpatient Hospital' },
  { code: '22', label: '22 — Outpatient Hospital' },
  { code: '23', label: '23 — Emergency Room' },
  { code: '49', label: '49 — Independent Clinic' },
  { code: '53', label: '53 — Community Mental Health Center' },
  { code: '99', label: '99 — Other' },
];

const DIAG_POINTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

const serviceLineSchema = z.object({
  serviceDate: z.string().optional().or(z.literal('')),
  cptCode: z.string().min(1, 'CPT code required').max(10),
  mod1: z.string().max(2).optional().or(z.literal('')),
  mod2: z.string().max(2).optional().or(z.literal('')),
  mod3: z.string().max(2).optional().or(z.literal('')),
  mod4: z.string().max(2).optional().or(z.literal('')),
  diagnosisPointers: z.array(z.string()).optional(),
  units: z.coerce.number().int().min(1).default(1),
  chargeAmount: z.coerce.number().min(0.01, 'Required'),
  description: z.string().optional().or(z.literal('')),
});

const schema = z.object({
  clientId: z.string().regex(UUID, 'Select a client'),
  invoiceId: z.string().optional().or(z.literal('')),
  payerName: z.string().min(1, 'Payer name is required'),
  payerId: z.string().optional().or(z.literal('')),
  subscriberId: z.string().optional().or(z.literal('')),
  claimNumber: z.string().optional().or(z.literal('')),
  submittedDate: z.string().optional().or(z.literal('')),
  diagnosisCodes: z.array(z.string()).optional(),
  placeOfService: z.string().optional().or(z.literal('')),
  renderingProviderNpi: z.string().max(10).optional().or(z.literal('')),
  serviceLines: z.array(serviceLineSchema).min(1, 'Add at least one service line'),
  notes: z.string().optional().or(z.literal('')),
});

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

const emptyLine = () => ({
  serviceDate: '', cptCode: '', mod1: '', mod2: '', mod3: '', mod4: '',
  diagnosisPointers: [], units: 1, chargeAmount: '', description: '',
});

export default function InsuranceClaimForm() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const seedAppointmentId = searchParams.get('appointmentId');

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
  });
  const clients = clientsData?.clients ?? [];

  const { data: existingData, isLoading: isLoadingClaim } = useQuery({
    queryKey: ['claim', id],
    queryFn: () => getClaim(id),
    enabled: isEditing,
  });

  const { data: feeScheduleData } = useQuery({
    queryKey: ['feeSchedule'],
    queryFn: getFeeScheduleItems,
  });
  const feeItems = feeScheduleData?.items ?? [];

  const { register, handleSubmit, watch, reset, setValue, control, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      clientId: '', invoiceId: '', payerName: '', payerId: '', subscriberId: '',
      claimNumber: '', submittedDate: '', diagnosisCodes: [''], placeOfService: '',
      renderingProviderNpi: '', notes: '', serviceLines: [emptyLine()],
    },
  });

  const { fields: diagFields, append: appendDiag, remove: removeDiag } = useFieldArray({ control, name: 'diagnosisCodes' });
  const { fields: lineFields, append: appendLine, remove: removeLine } = useFieldArray({ control, name: 'serviceLines' });

  const watchedLines = useWatch({ control, name: 'serviceLines' });

  // Auto-fill charge amount and description from fee schedule when CPT is entered
  const autoFillFromSchedule = (lineIndex, cptCode) => {
    if (!cptCode) return;
    const match = feeItems.find((item) => item.cptCode.toUpperCase() === cptCode.toUpperCase() && item.isActive);
    if (!match) return;
    const currentCharge = watchedLines?.[lineIndex]?.chargeAmount;
    if (!currentCharge) setValue(`serviceLines.${lineIndex}.chargeAmount`, parseFloat(match.standardRate));
    const currentDesc = watchedLines?.[lineIndex]?.description;
    if (!currentDesc && match.description) setValue(`serviceLines.${lineIndex}.description`, match.description);
  };
  const diagnosisCodes = watch('diagnosisCodes') || [];
  const totalBilled = (watchedLines || []).reduce(
    (sum, l) => sum + (parseFloat(l.chargeAmount) || 0) * (parseInt(l.units) || 1), 0
  );

  useEffect(() => {
    if (existingData?.claim) {
      const c = existingData.claim;
      const diagCodes = c.diagnosisCodes?.length > 0 ? c.diagnosisCodes : [''];
      const lines = c.serviceLines?.length > 0
        ? c.serviceLines.map((l) => ({
            serviceDate: l.serviceDate || '',
            cptCode: l.cptCode || '',
            mod1: l.modifiers?.[0] || '',
            mod2: l.modifiers?.[1] || '',
            mod3: l.modifiers?.[2] || '',
            mod4: l.modifiers?.[3] || '',
            diagnosisPointers: l.diagnosisPointers || [],
            units: l.units || 1,
            chargeAmount: l.chargeAmount || '',
            description: l.description || '',
          }))
        : [emptyLine()];

      reset({
        clientId: c.clientId || '', invoiceId: c.invoiceId || '',
        payerName: c.payerName || '', payerId: c.payerId || '',
        subscriberId: c.subscriberId || '', claimNumber: c.claimNumber || '',
        submittedDate: c.submittedDate || '', diagnosisCodes: diagCodes,
        placeOfService: c.placeOfService || '', renderingProviderNpi: c.renderingProviderNpi || '',
        notes: c.notes || '', serviceLines: lines,
      });
    }
  }, [existingData, reset]);

  const { data: seedApptData } = useQuery({
    queryKey: ['appointment', seedAppointmentId],
    queryFn: () => getAppointment(seedAppointmentId),
    enabled: Boolean(seedAppointmentId) && !isEditing,
  });

  // Pre-populate form from a linked appointment (new claim only)
  useEffect(() => {
    if (!seedApptData?.appointment || isEditing) return;
    const appt = seedApptData.appointment;
    const apptDate = appt.startTime ? appt.startTime.slice(0, 10) : '';
    const cptCode = appt.appointmentType?.cptCode || '';
    const chargeAmount = appt.appointmentType?.price != null ? parseFloat(appt.appointmentType.price) : '';
    const units = appt.durationMinutes ? Math.round(appt.durationMinutes / 15) : 1;
    const placeOfService = appt.isTelehealth ? '10' : '11';
    reset((prev) => ({
      ...prev,
      clientId: appt.clientId || '',
      placeOfService,
      serviceLines: [
        {
          serviceDate: apptDate,
          cptCode,
          mod1: '',
          mod2: '',
          mod3: '',
          mod4: '',
          diagnosisPointers: [],
          units,
          chargeAmount,
          description: appt.appointmentType?.name || '',
        },
      ],
    }));
  }, [seedApptData, isEditing, reset]);

  const selectedClientId = watch('clientId');

  const { data: selectedClientData } = useQuery({
    queryKey: ['client', selectedClientId],
    queryFn: () => getClient(selectedClientId),
    enabled: Boolean(selectedClientId) && !isEditing,
  });

  // Auto-fill payer name and subscriber ID from client insurance profile (new claims only)
  useEffect(() => {
    if (!selectedClientData || isEditing) return;
    const c = selectedClientData;
    if (c.insurancePlanName) setValue('payerName', c.insurancePlanName);
    if (c.insuranceMemberId) setValue('subscriberId', c.insuranceMemberId);
  }, [selectedClientData, isEditing, setValue]);

  const { data: invoicesData } = useQuery({
    queryKey: ['invoices', { clientId: selectedClientId }],
    queryFn: () => getInvoices({ clientId: selectedClientId, limit: 100 }),
    enabled: Boolean(selectedClientId),
  });
  const invoices = invoicesData?.invoices ?? [];

  const mutation = useMutation({
    mutationFn: (data) => isEditing ? updateClaim(id, data) : createClaim(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      if (isEditing) queryClient.invalidateQueries({ queryKey: ['claim', id] });
      toast.success(isEditing ? 'Claim updated' : 'Claim created');
      navigate(`/insurance/${isEditing ? id : result.claim.id}`);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save claim'),
  });

  const onSubmit = (data) => {
    mutation.mutate({
      clientId: data.clientId,
      invoiceId: data.invoiceId || null,
      payerName: data.payerName,
      payerId: data.payerId || null,
      subscriberId: data.subscriberId || null,
      claimNumber: data.claimNumber || null,
      submittedDate: data.submittedDate || null,
      diagnosisCodes: (data.diagnosisCodes || []).filter(Boolean),
      placeOfService: data.placeOfService || null,
      renderingProviderNpi: data.renderingProviderNpi || null,
      notes: data.notes || null,
      serviceLines: data.serviceLines.map((l) => ({
        serviceDate: l.serviceDate || null,
        cptCode: l.cptCode,
        modifiers: [l.mod1, l.mod2, l.mod3, l.mod4].filter(Boolean),
        diagnosisPointers: l.diagnosisPointers || [],
        units: parseInt(l.units || 1, 10),
        chargeAmount: parseFloat(l.chargeAmount),
        description: l.description || null,
      })),
    });
  };

  if (isEditing && isLoadingClaim) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to={isEditing ? `/insurance/${id}` : '/insurance'} className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          {isEditing ? 'Edit claim' : 'New claim'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Claim header */}
        <div className="card space-y-5">
          <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">Claim Info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Client *" error={errors.clientId}>
              <select className="input" {...register('clientId')} disabled={isEditing}>
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.lastName}, {c.firstName}</option>
                ))}
              </select>
            </Field>
            <Field label="Invoice (optional)" error={errors.invoiceId}>
              <select className="input" {...register('invoiceId')}>
                <option value="">None</option>
                {invoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>{inv.invoiceNumber} — ${inv.total}</option>
                ))}
              </select>
            </Field>
            <Field label="Payer name *" error={errors.payerName}>
              <input type="text" className="input" placeholder="e.g. Blue Cross Blue Shield" {...register('payerName')} />
            </Field>
            <Field label="Payer ID" error={errors.payerId}>
              <input type="text" className="input" placeholder="Payer NPI / code" {...register('payerId')} />
            </Field>
            <Field label="Subscriber ID" error={errors.subscriberId}>
              <input type="text" className="input" placeholder="Member / subscriber ID" {...register('subscriberId')} />
            </Field>
            <Field label="Place of service" error={errors.placeOfService}>
              <select className="input" {...register('placeOfService')}>
                <option value="">Select…</option>
                {PLACE_OF_SERVICE_CODES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="Rendering provider NPI" error={errors.renderingProviderNpi}>
              <input type="text" className="input" maxLength={10} placeholder="10-digit NPI" {...register('renderingProviderNpi')} />
            </Field>
            <Field label="Claim #" error={errors.claimNumber}>
              <input type="text" className="input" placeholder="Assigned by payer" {...register('claimNumber')} />
            </Field>
            <Field label="Submitted date" error={errors.submittedDate}>
              <input type="date" className="input" {...register('submittedDate')} />
            </Field>
          </div>
        </div>

        {/* Diagnosis codes */}
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">
            Diagnosis Codes (ICD-10)
          </h2>
          <div className="space-y-2">
            {diagFields.map((field, i) => (
              <div key={field.id} className="flex items-center gap-2">
                <span className="text-xs font-mono font-bold text-neutral-500 w-5">{DIAG_POINTERS[i]}</span>
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="ICD-10 code (e.g. F32.1)"
                  {...register(`diagnosisCodes.${i}`)}
                />
                {diagFields.length > 1 && (
                  <button type="button" onClick={() => removeDiag(i)} className="p-1.5 text-neutral-400 hover:text-danger-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {diagFields.length < 12 && (
            <button
              type="button"
              onClick={() => appendDiag('')}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" /> Add diagnosis code
            </button>
          )}
        </div>

        {/* Service lines */}
        <div className="card space-y-4">
          <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">
            Service Lines
          </h2>
          {errors.serviceLines?.root && (
            <p className="text-sm text-danger-600">{errors.serviceLines.root.message}</p>
          )}
          {lineFields.map((field, i) => (
            <div key={field.id} className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Line {i + 1}</span>
                {lineFields.length > 1 && (
                  <button type="button" onClick={() => removeLine(i)} className="p-1 text-neutral-400 hover:text-danger-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Service date" error={errors.serviceLines?.[i]?.serviceDate}>
                  <input type="date" className="input" {...register(`serviceLines.${i}.serviceDate`)} />
                </Field>
                <Field label="CPT code *" error={errors.serviceLines?.[i]?.cptCode}>
                  {(() => {
                    const cptReg = register(`serviceLines.${i}.cptCode`);
                    const cptVal = watchedLines?.[i]?.cptCode?.toUpperCase();
                    const feeItem = feeItems.find((f) => f.cptCode === cptVal && f.isActive);
                    return (
                      <>
                        <input
                          type="text"
                          className="input font-mono"
                          maxLength={10}
                          placeholder="e.g. 90837"
                          {...cptReg}
                          onBlur={(e) => { cptReg.onBlur(e); autoFillFromSchedule(i, e.target.value); }}
                        />
                        {feeItem && !feeItem.isCovered && (
                          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                            Non-covered — bill client directly
                          </p>
                        )}
                      </>
                    );
                  })()}
                </Field>
                <Field label="Units" error={errors.serviceLines?.[i]?.units}>
                  <input type="number" className="input" min={1} {...register(`serviceLines.${i}.units`)} />
                </Field>
                <Field label="Charge $" error={errors.serviceLines?.[i]?.chargeAmount}>
                  <input type="number" step="0.01" min="0.01" className="input" {...register(`serviceLines.${i}.chargeAmount`)} />
                </Field>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {['mod1', 'mod2', 'mod3', 'mod4'].map((mod, mi) => (
                  <Field key={mod} label={mi === 0 ? 'Modifiers' : '\u00A0'} error={errors.serviceLines?.[i]?.[mod]}>
                    <input type="text" className="input font-mono uppercase" maxLength={2} placeholder={`M${mi + 1}`} {...register(`serviceLines.${i}.${mod}`)} />
                  </Field>
                ))}
              </div>
              {diagnosisCodes.filter(Boolean).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Diagnosis pointers</label>
                  <div className="flex flex-wrap gap-3">
                    {diagnosisCodes.filter(Boolean).map((code, di) => (
                      <label key={di} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          value={DIAG_POINTERS[di]}
                          className="h-3.5 w-3.5 rounded border-neutral-300 text-primary-600"
                          {...register(`serviceLines.${i}.diagnosisPointers`)}
                        />
                        <span className="font-mono font-bold text-xs text-primary-600">{DIAG_POINTERS[di]}</span>
                        <span className="text-neutral-500 text-xs">({code})</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Field label="Description (optional)" error={errors.serviceLines?.[i]?.description}>
                <input type="text" className="input" placeholder="Service description" {...register(`serviceLines.${i}.description`)} />
              </Field>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => appendLine(emptyLine())}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" /> Add service line
            </button>
            {totalBilled > 0 && (
              <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Total billed: {fmt(totalBilled)}
              </span>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <Field label="Notes" error={errors.notes}>
            <textarea className="input" rows={3} {...register('notes')} />
          </Field>
        </div>

        <div className="flex justify-end gap-3">
          <Link to={isEditing ? `/insurance/${id}` : '/insurance'} className="btn btn-secondary">
            Cancel
          </Link>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary disabled:opacity-60">
            {mutation.isPending ? 'Saving…' : isEditing ? 'Save changes' : 'Create claim'}
          </button>
        </div>
      </form>
    </div>
  );
}
